// Drawing App - Real-time Collaborative Drawing

import { Point, BrushSettings, BrushPresetType } from './types';
import { CanvasEngine } from './canvas_engine';
import { BrushEngine } from './brush_engine';

interface RemoteCursor {
  userId: string;
  userName: string;
  x: number;
  y: number;
  color: string;
  lastUpdate: number;
}

interface StrokeData {
  userId: string;
  points: { x: number; y: number; pressure: number }[];
  brush: Partial<BrushSettings>;
  color: string;
  preset: BrushPresetType;
}

export class RealtimeSync {
  private roomCode: string;
  private canvasEngine: CanvasEngine;
  private overlayCanvas: OffscreenCanvas;
  private overlayCtx: OffscreenCanvasRenderingContext2D;
  private remoteCursors: Map<string, RemoteCursor> = new Map();
  private remoteBrushEngine: BrushEngine;
  private pusherChannel: any = null;
  private cursorBroadcastInterval: number | null = null;
  private cursorCleanupInterval: number | null = null;
  private localCursorPos: { x: number; y: number } | null = null;
  private canvasWidth: number;
  private canvasHeight: number;
  private userId: string;
  private userName: string;

  // Assign colors to remote users
  private userColors: Map<string, string> = new Map();
  private colorPalette = [
    '#FF6B6B', '#4ECDC4', '#FFE66D', '#A8E6CF',
    '#FF8B94', '#98D8C8', '#F7DC6F', '#BB8FCE',
    '#85C1E9', '#F1948A', '#82E0AA', '#F8C471'
  ];
  private colorIndex = 0;

  constructor(
    roomCode: string,
    canvasEngine: CanvasEngine,
    canvasWidth: number,
    canvasHeight: number,
    userId: string,
    userName: string
  ) {
    this.roomCode = roomCode;
    this.canvasEngine = canvasEngine;
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.userId = userId;
    this.userName = userName;
    this.remoteBrushEngine = new BrushEngine();

    this.overlayCanvas = new OffscreenCanvas(canvasWidth, canvasHeight);
    this.overlayCtx = this.overlayCanvas.getContext('2d')!;

    this.connect();
  }

  private connect(): void {
    // Use Pusher if available
    if (typeof (window as any).Pusher === 'undefined') {
      console.warn('[RealtimeSync] Pusher not available, real-time sync disabled');
      return;
    }

    const pusher = (window as any).__pusher;
    if (!pusher) {
      console.warn('[RealtimeSync] Pusher instance not found');
      return;
    }

    const channelName = `presence-drawing-${this.roomCode}`;
    this.pusherChannel = pusher.subscribe(channelName);

    this.pusherChannel.bind('pusher:subscription_succeeded', () => {
      console.log('[RealtimeSync] Connected to drawing channel');
    });

    // Listen for remote strokes
    this.pusherChannel.bind('client-stroke', (data: StrokeData) => {
      if (data.userId === this.userId) return;
      this.renderRemoteStroke(data);
    });

    // Listen for cursor updates
    this.pusherChannel.bind('client-cursor', (data: { userId: string; userName: string; x: number; y: number }) => {
      if (data.userId === this.userId) return;
      this.updateRemoteCursor(data);
    });

    // Listen for member removal
    this.pusherChannel.bind('pusher:member_removed', (member: any) => {
      this.remoteCursors.delete(member.id);
      this.renderOverlay();
    });

    // Start cursor broadcast
    this.cursorBroadcastInterval = window.setInterval(() => {
      this.broadcastCursor();
    }, 100);

    // Clean up stale cursors
    this.cursorCleanupInterval = window.setInterval(() => {
      const now = Date.now();
      let changed = false;
      this.remoteCursors.forEach((cursor, id) => {
        if (now - cursor.lastUpdate > 5000) {
          this.remoteCursors.delete(id);
          changed = true;
        }
      });
      if (changed) this.renderOverlay();
    }, 2000);
  }

  updateLocalCursor(x: number, y: number): void {
    this.localCursorPos = { x, y };
  }

  broadcastStroke(points: { x: number; y: number; pressure: number }[], brush: BrushSettings, color: string, preset: BrushPresetType): void {
    if (!this.pusherChannel) return;

    // Downsample points if too many
    let sampledPoints = points;
    if (points.length > 100) {
      const step = Math.ceil(points.length / 100);
      sampledPoints = points.filter((_, i) => i % step === 0 || i === points.length - 1);
    }

    const data: StrokeData = {
      userId: this.userId,
      points: sampledPoints,
      brush: {
        size: brush.size,
        opacity: brush.opacity,
        flow: brush.flow,
        spacing: brush.spacing,
        hardness: brush.hardness,
        jitter: brush.jitter,
      },
      color,
      preset,
    };

    try {
      this.pusherChannel.trigger('client-stroke', data);
    } catch (e) {
      // Fallback: send via server
      this.sendViaServer('stroke', data);
    }
  }

  private broadcastCursor(): void {
    if (!this.pusherChannel || !this.localCursorPos) return;

    try {
      this.pusherChannel.trigger('client-cursor', {
        userId: this.userId,
        userName: this.userName,
        x: this.localCursorPos.x,
        y: this.localCursorPos.y,
      });
    } catch (e) {
      // Client events may not be enabled, ignore
    }
  }

  private async sendViaServer(type: string, data: any): Promise<void> {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    try {
      await fetch(`/drawing/room/${this.roomCode}/signal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ type, data }),
      });
    } catch (e) {
      console.error('[RealtimeSync] Failed to send signal:', e);
    }
  }

  private getUserColor(userId: string): string {
    if (!this.userColors.has(userId)) {
      this.userColors.set(userId, this.colorPalette[this.colorIndex % this.colorPalette.length]);
      this.colorIndex++;
    }
    return this.userColors.get(userId)!;
  }

  private updateRemoteCursor(data: { userId: string; userName: string; x: number; y: number }): void {
    this.remoteCursors.set(data.userId, {
      userId: data.userId,
      userName: data.userName,
      x: data.x,
      y: data.y,
      color: this.getUserColor(data.userId),
      lastUpdate: Date.now(),
    });
    this.renderOverlay();
  }

  private renderRemoteStroke(data: StrokeData): void {
    if (data.points.length === 0) return;

    const brush: BrushSettings = {
      size: data.brush.size || 12,
      opacity: data.brush.opacity || 0.8,
      flow: data.brush.flow || 1.0,
      spacing: data.brush.spacing || 0.15,
      hardness: data.brush.hardness || 0.8,
      jitter: data.brush.jitter || 0,
      smoothing: 0,
      symmetry: 'none',
      texture: null,
    };

    this.remoteBrushEngine.beginStroke(this.canvasWidth, this.canvasHeight);

    for (const pt of data.points) {
      this.remoteBrushEngine.addPoint(
        { x: pt.x, y: pt.y, pressure: pt.pressure, tiltX: 0, tiltY: 0, timestamp: 0 },
        brush,
        data.color
      );
    }

    this.remoteBrushEngine.endStroke(this.overlayCtx, brush.opacity, 'source-over');
    this.canvasEngine.setRemoteOverlay(this.overlayCanvas);
    this.canvasEngine.requestRender();
  }

  private renderOverlay(): void {
    // Re-render cursors on overlay (we don't clear stroke data)
    // Instead, we use a separate approach: render cursors in canvas engine's render loop
    this.canvasEngine.requestRender();
  }

  getChannel(): any {
    return this.pusherChannel;
  }

  getRemoteCursors(): RemoteCursor[] {
    return Array.from(this.remoteCursors.values());
  }

  clearOverlay(): void {
    this.overlayCtx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
    this.canvasEngine.setRemoteOverlay(this.overlayCanvas);
    this.canvasEngine.requestRender();
  }

  destroy(): void {
    if (this.cursorBroadcastInterval) {
      clearInterval(this.cursorBroadcastInterval);
    }
    if (this.cursorCleanupInterval) {
      clearInterval(this.cursorCleanupInterval);
    }
    if (this.pusherChannel) {
      this.pusherChannel.unbind_all();
      const pusher = (window as any).__pusher;
      if (pusher) {
        pusher.unsubscribe(`presence-drawing-${this.roomCode}`);
      }
    }
    this.canvasEngine.setRemoteOverlay(null);
  }
}
